// v2 reindeer — the full tundra build: a deep two-tone body with a pale rump
// patch, dark legs on black hooves, a thick angled neck carrying the signature
// white hanging neck mane (dewlap), flared ears — and big sweeping antlers: a
// back-then-forward curved main beam, the forward brow tine over the face and a
// forked crown, five bent segments a side. Two coats: grey-brown winter and a
// browner summer. Ported 1:1 from age-of-mcp's v2/reindeer.ts, using the
// library's shared `critter` (quadLegs) and `wildland` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, critter, wildland, type Part } from "../../lib";

const COATS = [
  { body: "#6b5d4c", flank: "#7d7060", mane: "#ddd5c4", head: "#756755",
    antler: "#c9b68c", leg: "#564a3c", hoof: "#241e16", rump: "#e6dfcf" },
  { body: "#7a6448", flank: "#8d775a", mane: "#d8cdb8", head: "#84705a",
    antler: "#c2ab7e", leg: "#5e4c38", hoof: "#241e16", rump: "#e2d8c2" },
];

function reindeerParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.07, dz: 0.034, bodyY: 0.165, rTop: 0.0095, rFoot: 0.007,
    color: c.leg, hoof: { color: c.hoof, r: 0.0098, h: 0.016 },
  });
  parts.push(
    // two-tone torso: chest mass, barrel, rump with the pale patch
    { geometry: new THREE.IcosahedronGeometry(0.058, 0), color: c.body,
      position: [0, 0.165, 0], scale: [1.6, 0.98, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.046, 0), color: c.flank,
      position: [0.055, 0.16, 0], scale: [1.0, 1.05, 0.92] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.body,
      position: [-0.065, 0.17, 0], scale: [1, 0.95, 0.88] },
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.rump,
      position: [-0.098, 0.175, 0], scale: [0.55, 0.9, 1.0] },
    // thick angled neck + the white hanging neck mane (dewlap)
    wildland.tube([0.085, 0.19, 0], [0.118, 0.237, 0], 0.023, 0.0175, c.body),
    { geometry: new THREE.IcosahedronGeometry(0.0195, 0), color: c.mane,
      position: [0.1, 0.165, 0], scale: [1.1, 1.3, 0.7] },
    // head, muzzle, nose
    { geometry: new THREE.IcosahedronGeometry(0.026, 0), color: c.head,
      position: [0.13, 0.247, 0], scale: [1.3, 0.95, 0.85] },
    { geometry: new THREE.ConeGeometry(0.0115, 0.03, 4), color: c.head,
      position: [0.16, 0.24, 0], rotation: [0, 0, -Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.0055, 0), color: "#2e2820",
      position: [0.176, 0.239, 0] },
    // tail
    { geometry: new THREE.IcosahedronGeometry(0.009, 0), color: c.mane,
      position: [-0.105, 0.185, 0] },
  );
  for (const s of [1, -1] as const) {
    parts.push(
      // flared ears
      { geometry: new THREE.ConeGeometry(0.0065, 0.018, 4), color: c.head,
        position: [0.118, 0.266, s * 0.017], rotation: [s * 0.9, 0, 0] },
      // antlers — back-swept beam curving forward, brow tine, crown fork
      wildland.tube([0.124, 0.268, s * 0.012], [0.098, 0.318, s * 0.02],
        0.0042, 0.0036, c.antler),
      wildland.tube([0.098, 0.318, s * 0.02], [0.118, 0.352, s * 0.026],
        0.0036, 0.0022, c.antler),
      wildland.tube([0.116, 0.288, s * 0.015], [0.142, 0.302, s * 0.024],
        0.0028, 0.0014, c.antler),
      wildland.tube([0.118, 0.352, s * 0.026], [0.104, 0.368, s * 0.034],
        0.002, 0.001, c.antler),
      wildland.tube([0.118, 0.352, s * 0.026], [0.134, 0.366, s * 0.03],
        0.002, 0.001, c.antler),
    );
  }
  parts.push(...wildland.eyes(0.139, 0.255, 0.013, 0.0035, "#16130e"));
  return parts;
}

export const reindeerV2 = defineModel({
  id: "reindeer-v2",
  batchId: "fable",
  name: "Reindeer (v2)",
  tags: ["creature","reindeer","low-poly","age-of-mcp","fable"],
  summary: "Low-poly reindeer.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.15,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => reindeerParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => reindeerParts(1) },
  ],
  animations: [],
});
